import { array as codecArray } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, paginated, requestParams } from '@codegrade/api-core';
import { CreateGroupGroupSetData, ExtendedGroup, Group, GroupSet } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function groupSet(config) {
    const copyGroupSetResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(GroupSet)),
    });
    const createGroupResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedGroup)),
    });
    const deleteResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const getResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(GroupSet)),
    });
    const getAllGroupsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(Group))),
    });
    const getUserGroupResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedGroup)),
    });
    return Object.freeze({
        /**
         * Copy a GroupSet with all its groups and members.
         *
         * This endpoint creates a new group set with the same constraints (min, max), groups and members as the original one. Each copied group receives a new virtual user and has "(copy)" appended to its name.
         *
         * @param arg The arguments to the function.
         * @param arg.groupSetId The id of the group set to copy.
         * @returns The newly created group set.
         */
        copyGroupSet: async ({ groupSetId, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/group_sets/${groupSetId}/copy`, copyGroupSetResponse.v, undefined);
            return result;
        },
        /**
         * Create a group for the given group set.
         *
         * @param arg The arguments to the function.
         * @param arg.groupSetId The id of the group set where the new group should be placed in.
         * @param arg.requestBody The body of the request.
         * @returns The newly created group.
         */
        createGroup: async ({ groupSetId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/group_sets/${groupSetId}/group`, createGroupResponse.v, JSON.stringify(CreateGroupGroupSetData.encode(requestBody)));
            return result;
        },
        /**
         * Delete a `GroupSet`.
         *
         * You can only delete a group set if there are no groups in the set and no assignment is connected to the group set.
         *
         * @param arg The arguments to the function.
         * @param arg.groupSetId The id of the group set
         * @returns Nothing.
         */
        delete: async ({ groupSetId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/group_sets/${groupSetId}`, deleteResponse.v, undefined);
            return result;
        },
        /**
         * Get a single `GroupSet` by id.
         *
         * @param arg The arguments to the function.
         * @param arg.groupSetId The id of the group set
         * @returns A response containing the JSON serialized group set.
         */
        get: async ({ groupSetId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/group_sets/${groupSetId}`, getResponse.v, undefined);
            return result;
        },
        /**
         * Get all groups for a given group set.
         *
         * @param arg The arguments to the function.
         * @param arg.groupSetId The group set for which the groups should be returned.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns All the groups for the given group set.
         */
        getAllGroups: async ({ groupSetId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/group_sets/${groupSetId}/groups/`, getAllGroupsResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get the group for a specific user within a given group set.
         *
         * @param arg The arguments to the function.
         * @param arg.groupSetId The group set to search within.
         * @param arg.userId The ID of the user whose group is to be found.
         * @returns The single group object for the given user.
         */
        getUserGroup: async ({ groupSetId, userId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/group_sets/${groupSetId}/user/${userId}/group`, getUserGroupResponse.v, undefined);
            return result;
        },
    });
}
